import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth, PinnedNavigationItem } from './AuthContext';

// Pinned item type (matches the PinnedNavigationItem interface from AuthContext)
export interface PinnedItem {
  id?: number; // Optional for new items before they're saved
  item_id: string;
  name: string;
  route: string;
  icon: string;
  section: string;
  category: string;
}

// State interface
interface PinnedItemsState {
  pinnedItems: PinnedNavigationItem[];
  temporaryItem: PinnedItem | null;
  loading: boolean;
  error: string | null;
}

// Action types
type PinnedItemsAction =
  | { type: 'SET_PINNED_ITEMS'; payload: PinnedNavigationItem[] }
  | { type: 'PIN_ITEM'; payload: PinnedNavigationItem }
  | { type: 'UNPIN_ITEM'; payload: number }
  | { type: 'SET_TEMPORARY_ITEM'; payload: PinnedItem | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

// Reducer
const pinnedItemsReducer = (state: PinnedItemsState, action: PinnedItemsAction): PinnedItemsState => {
  switch (action.type) {
    case 'SET_PINNED_ITEMS':
      return { ...state, pinnedItems: action.payload, error: null };
    case 'PIN_ITEM':
      return { ...state, pinnedItems: [...state.pinnedItems, action.payload], error: null };
    case 'UNPIN_ITEM':
      return { ...state, pinnedItems: state.pinnedItems.filter(item => item.id !== action.payload), error: null };
    case 'SET_TEMPORARY_ITEM':
      return { ...state, temporaryItem: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

// Context interface
interface PinnedItemsContextType {
  pinnedItems: PinnedNavigationItem[];
  temporaryItem: PinnedItem | null;
  loading: boolean;
  error: string | null;
  pinItem: (item: PinnedItem) => Promise<void>;
  unpinItem: (itemId: string) => Promise<void>;
  isPinned: (itemId: string) => boolean;
  setTemporaryItem: (item: PinnedItem | null) => void;
  clearTemporaryItem: () => void;
  clearError: () => void;
}

const PinnedItemsContext = createContext<PinnedItemsContextType | undefined>(undefined);

export const usePinnedItems = () => {
  const context = useContext(PinnedItemsContext);
  if (!context) {
    throw new Error('usePinnedItems must be used within a PinnedItemsProvider');
  }
  return context;
};

interface PinnedItemsProviderProps {
  children: React.ReactNode;
}

export const PinnedItemsProvider: React.FC<PinnedItemsProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [state, dispatch] = useReducer(pinnedItemsReducer, {
    pinnedItems: [],
    temporaryItem: null,
    loading: false,
    error: null,
  });

  // Load pinned items from user object (already loaded by AuthContext)
  useEffect(() => {
    console.log('[PinnedItemsContext] useEffect triggered');
    console.log('[PinnedItemsContext] isAuthenticated:', isAuthenticated);
    console.log('[PinnedItemsContext] user:', user);
    console.log('[PinnedItemsContext] user?.pinned_items:', user?.pinned_items);

    if (isAuthenticated && user?.pinned_items) {
      console.log('[PinnedItemsContext] Setting pinned items:', user.pinned_items.length);
      dispatch({ type: 'SET_PINNED_ITEMS', payload: user.pinned_items });
    } else if (!isAuthenticated) {
      console.log('[PinnedItemsContext] Not authenticated, clearing items');
      dispatch({ type: 'SET_PINNED_ITEMS', payload: [] });
    } else {
      console.log('[PinnedItemsContext] Authenticated but no pinned_items on user object');
    }
  }, [user, isAuthenticated]);

  /**
   * Pin an item
   */
  const pinItem = useCallback(
    async (item: PinnedItem) => {
      if (!isAuthenticated) {
        dispatch({ type: 'SET_ERROR', payload: 'You must be logged in to pin items' });
        return;
      }

      // Check if already pinned
      const alreadyPinned = state.pinnedItems.some(p => p.item_id === item.item_id);
      if (alreadyPinned) {
        dispatch({ type: 'SET_ERROR', payload: 'This item is already pinned' });
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      // Calculate next order value (place at end)
      const maxOrder = state.pinnedItems.length > 0
        ? Math.max(...state.pinnedItems.map(p => p.order))
        : 0;

      const requestPayload = {
        item_id: item.item_id,
        name: item.name,
        route: item.route,
        icon: item.icon || '',
        section: item.section,
        category: item.category || '',
        order: maxOrder + 1,
      };

      try {
        console.log('[PinnedItemsContext] Attempting to pin:', requestPayload);

        const response = await axios.post('/api/navigation/pinned/', requestPayload);
        const newPinnedItem = response.data as PinnedNavigationItem;

        console.log('[PinnedItemsContext] Item pinned successfully:', newPinnedItem);

        // Add to state
        dispatch({ type: 'PIN_ITEM', payload: newPinnedItem });

        console.log('[PinnedItemsContext] State updated, new count should be:', state.pinnedItems.length + 1);
      } catch (err: any) {
        console.error('Error pinning item:', err);
        console.error('Error response data:', err.response?.data);
        console.error('Error status:', err.response?.status);
        console.error('Payload that was sent:', requestPayload);

        // Log the actual error messages
        if (err.response?.data?.non_field_errors) {
          console.error('Non-field errors:', err.response.data.non_field_errors);
        }

        const errorMessage = err.response?.data?.non_field_errors?.[0] ||
                            err.response?.data?.item_id?.[0] ||
                            err.response?.data?.error ||
                            'Failed to pin item';
        console.error('Final error message:', errorMessage);
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw new Error(errorMessage);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [isAuthenticated, state.pinnedItems]
  );

  /**
   * Unpin an item by item_id
   */
  const unpinItem = useCallback(
    async (itemId: string) => {
      if (!isAuthenticated) {
        dispatch({ type: 'SET_ERROR', payload: 'You must be logged in to unpin items' });
        return;
      }

      const pinnedItem = state.pinnedItems.find(p => p.item_id === itemId);
      if (!pinnedItem) {
        dispatch({ type: 'SET_ERROR', payload: 'Item not found in pinned items' });
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      try {
        await axios.delete(`/api/navigation/pinned/${pinnedItem.id}/`);

        // Remove from state
        dispatch({ type: 'UNPIN_ITEM', payload: pinnedItem.id });
      } catch (err: any) {
        console.error('Error unpinning item:', err);
        const errorMessage = err.response?.data?.error || 'Failed to unpin item';
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        throw new Error(errorMessage);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [isAuthenticated, state.pinnedItems]
  );

  /**
   * Check if an item is pinned
   */
  const isPinned = useCallback(
    (itemId: string): boolean => {
      return state.pinnedItems.some(p => p.item_id === itemId);
    },
    [state.pinnedItems]
  );

  /**
   * Set temporary item (appears below "More Items" in sidebar)
   */
  const setTemporaryItem = useCallback((item: PinnedItem | null) => {
    dispatch({ type: 'SET_TEMPORARY_ITEM', payload: item });
  }, []);

  /**
   * Clear temporary item
   */
  const clearTemporaryItem = useCallback(() => {
    dispatch({ type: 'SET_TEMPORARY_ITEM', payload: null });
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value: PinnedItemsContextType = {
    pinnedItems: state.pinnedItems,
    temporaryItem: state.temporaryItem,
    loading: state.loading,
    error: state.error,
    pinItem,
    unpinItem,
    isPinned,
    setTemporaryItem,
    clearTemporaryItem,
    clearError,
  };

  return (
    <PinnedItemsContext.Provider value={value}>
      {children}
    </PinnedItemsContext.Provider>
  );
};
